"use client";

import Link from "next/link";
import KininaruButton from "@/components/people/KininaruButton";

export type PersonItem = {
  id: string;
  name: string;
  title?: string;
  quote?: string;
  playStyle?: string;
  intro?: string;
  image?: string | null;
  regions?: string[];
  badge?: string;
  badges?: string[];
  category?: string;
  displayScore?: number;
  isClaimed?: boolean;
  isOwnProfile?: boolean;
  practiceCount?: number;
  discoveryCount?: number;
  rep?: string;
  links?: string[];
};

export default function PeopleCard({ item }: { item: PersonItem }) {
  return (
    <div
      id={`profile-${item.id}`}
      style={{
        border: item.isOwnProfile ? "1px solid #c8b8e8" : "1px solid #e8e8e8",
        borderRadius: 10,
        padding: 16,
        background: item.isOwnProfile ? "#fbfaff" : "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* Avatar + name/title */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <div
            style={{ width: 44, height: 44, borderRadius: "50%", background: "#f0f0f0", flexShrink: 0 }}
          />
        )}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{item.name}</span>
            {item.isOwnProfile && (
              <span style={{ fontSize: 10, color: "#9b7ec8", border: "1px solid #c8b8e8", borderRadius: 4, padding: "1px 5px", lineHeight: 1.4 }}>あなた</span>
            )}
          </div>
          {item.title && (
            <div style={{ fontSize: 12, color: "#888", lineHeight: 1.4 }}>{item.title}</div>
          )}
        </div>
      </div>

      {/* Quote — empty-safe, immediately after name for personal flow */}
      {item.quote && (
        <div style={{ fontSize: 14, color: "#333", fontStyle: "italic", lineHeight: 1.6 }}>
          「{item.quote}」
        </div>
      )}

      {/* Region chip + category badge — after quote, contextual metadata */}
      {(item.regions?.length || item.badges?.length || item.badge || item.category) && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {item.regions?.[0] && (
            <span style={{ fontSize: 11, padding: "2px 8px", border: "1px solid #e0e0e0", borderRadius: 20, color: "#666" }}>
              {item.regions[0]}
            </span>
          )}
          {(item.badges && item.badges.length > 0
            ? item.badges
            : item.badge ? [item.badge] : item.category ? [item.category] : []
          ).map((b) => (
            <span key={b} style={{ fontSize: 11, padding: "2px 8px", background: "#f5f5f5", borderRadius: 20, color: "#555" }}>
              {b}
            </span>
          ))}
        </div>
      )}

      {/* Play style — empty-safe */}
      {item.playStyle && (
        <div style={{ fontSize: 12, color: "#666", lineHeight: 1.5 }}>
          {item.playStyle}
        </div>
      )}

      {/* Intro — empty-safe */}
      {item.intro && (
        <div style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>
          {item.intro}
        </div>
      )}

      {/* 代表リンク・参考URL */}
      {(item.rep || (item.links && item.links.length > 0)) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {item.rep && (
            <a
              href={item.rep}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 11, color: "#4a90e2", textDecoration: "underline" }}
            >
              🔗 {(() => { try { return new URL(item.rep).hostname; } catch { return item.rep; } })()}
            </a>
          )}
          {item.links?.map((url, i) => (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 11, color: "#4a90e2", textDecoration: "underline" }}
            >
              🔗 {(() => { try { return new URL(url).hostname; } catch { return url; } })()}
            </a>
          ))}
        </div>
      )}

      {/* Action */}
      <div style={{ marginTop: "auto", paddingTop: 8 }}>
        <KininaruButton profileId={item.id} />
        {(item.displayScore ?? 0) > 0 && (
          <div style={{ fontSize: 11, color: item.isOwnProfile ? "#9b7ec8" : "#aaa", marginTop: 6 }}>
            👀 {item.displayScore}{item.isOwnProfile ? "\u3000反応あり" : ""}
          </div>
        )}
        {((item.practiceCount ?? 0) > 0 || (item.discoveryCount ?? 0) > 0) && (
          <div style={{ fontSize: 11, color: "#bbb", marginTop: 4, display: "flex", gap: 10 }}>
            {(item.practiceCount ?? 0) > 0 && (
              <Link href={`/people/${item.id}`} style={{ color: "#bbb", textDecoration: "none" }}>
                実践レポート {item.practiceCount}
              </Link>
            )}
            {(item.discoveryCount ?? 0) > 0 && (
              <Link href={`/people/${item.id}`} style={{ color: "#bbb", textDecoration: "none" }}>
                発見ノート {item.discoveryCount}
              </Link>
            )}
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <Link href={`/people/${item.id}`} style={{ fontSize: 11, color: "#999", textDecoration: "none" }}>
            くわしく見る →
          </Link>
        </div>
      </div>
    </div>
  );
}
